import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const API_URL = '';

/* Role → route mapping */
const ROLE_ROUTES = {
  owner: '/',
  admin: '/',
  superadmin: '/superadmin',
  auditor: '/',
  cliente: '/',
  viewer: '/',
  vigilante: '/',
};

/* Yup validation */
const validationSchema = Yup.object({
  email: Yup.string()
    .min(3, 'Ingresa tu usuario o email')
    .required('El usuario o email es requerido'),
  password: Yup.string()
    .min(6, 'Mínimo 6 caracteres')
    .required('La contraseña es requerida'),
});

/* ─── Animated background ────────────────────────────────── */
const AnimatedBackground = () => {
  const orbs = [
    { size: 280, left: '8%', top: '18%', delay: '0s', dur: '7s' },
    { size: 360, left: '72%', top: '65%', delay: '1s', dur: '9s' },
    { size: 220, left: '45%', top: '80%', delay: '2s', dur: '6s' },
    { size: 310, left: '85%', top: '20%', delay: '0.5s', dur: '8s' },
    { size: 190, left: '22%', top: '60%', delay: '1.5s', dur: '10s' },
  ];

  const cameras = [
    { top: '8%', left: '6%', rot: -15, size: 22, opacity: 0.18 },
    { top: '7%', right: '7%', rot: 15, size: 22, opacity: 0.18 },
    { bottom: '9%', left: '5%', rot: 15, size: 20, opacity: 0.14 },
    { bottom: '8%', right: '6%', rot: -15, size: 20, opacity: 0.14 },
    { top: '42%', left: '2%', rot: 0, size: 18, opacity: 0.10 },
    { top: '42%', right: '2%', rot: 0, size: 18, opacity: 0.10 },
  ];

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0 }}>
      {/* Grid */}
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(rgba(111,255,233,0.035) 1px, transparent 1px),
          linear-gradient(90deg, rgba(111,255,233,0.035) 1px, transparent 1px)
        `,
        backgroundSize: '55px 55px',
      }} />

      {/* Glow orbs */}
      {orbs.map((o, i) => (
        <Box key={i} sx={{
          position: 'absolute',
          borderRadius: '50%',
          width: o.size, height: o.size,
          left: o.left, top: o.top,
          background: i % 2 === 0
            ? 'radial-gradient(circle, rgba(111,255,233,0.13) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(91,192,190,0.09) 0%, transparent 70%)',
          transform: 'translate(-50%,-50%)',
          animation: `orbFloat ${o.dur} ease-in-out ${o.delay} infinite alternate`,
          '@keyframes orbFloat': {
            '0%': { transform: 'translate(-50%,-50%) scale(1)' },
            '100%': { transform: 'translate(-50%,-58%) scale(1.08)' },
          },
        }} />
      ))}

      {/* Scan line */}
      <Box sx={{
        position: 'absolute', width: '100%', height: '1.5px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(111,255,233,0.55) 50%, transparent 100%)',
        animation: 'scanLine 9s linear infinite',
        '@keyframes scanLine': {
          '0%': { top: '-2px', opacity: 0 },
          '4%': { opacity: 1 },
          '96%': { opacity: 1 },
          '100%': { top: '100%', opacity: 0 },
        },
      }} />

      {/* Corner camera icons */}
      {cameras.map((c, i) => (
        <VideocamIcon key={i} sx={{
          position: 'absolute',
          ...c,
          fontSize: c.size,
          color: `rgba(111,255,233,${c.opacity})`,
          transform: `rotate(${c.rot}deg)`,
        }} />
      ))}
    </Box>
  );
};

/* ─── Role Badge ─────────────────────────────────────────── */
const RoleBadge = ({ role }) => {
  const colors = {
    owner: { bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.4)', text: '#a78bfa', label: 'OWNER' },
    admin: { bg: 'rgba(255,82,82,0.15)', border: 'rgba(255,82,82,0.4)', text: '#FF5252', label: 'ADMIN' },
    superadmin: { bg: 'rgba(56,189,248,0.15)', border: 'rgba(56,189,248,0.45)', text: '#38bdf8', label: 'SUPERADMIN' },
    auditor: { bg: 'rgba(245,158,11,0.13)', border: 'rgba(245,158,11,0.35)', text: '#f59e0b', label: 'AUDITOR' },
    cliente: { bg: 'rgba(255,215,0,0.12)', border: 'rgba(255,215,0,0.35)', text: '#FFD700', label: 'CLIENTE' },
    viewer: { bg: 'rgba(111,255,233,0.12)', border: 'rgba(111,255,233,0.35)', text: '#6FFFE9', label: 'VIEWER' },
    vigilante: { bg: 'rgba(56,189,248,0.12)', border: 'rgba(56,189,248,0.35)', text: '#38bdf8', label: 'VIGILANTE' },
  };
  const c = colors[role] || colors.viewer;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.4, py: 0.4, borderRadius: '6px',
      background: c.bg, border: `1px solid ${c.border}`,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: c.text, boxShadow: `0 0 6px ${c.text}` }} />
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: c.text, letterSpacing: '1.5px' }}>
        {c.label}
      </Typography>
    </Box>
  );
};

/* ─── Input SX shared styles ─────────────────────────────── */
const makeInputSx = (mb = 2.5) => ({
  mb,
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    background: 'rgba(255,255,255,0.045)',
    borderRadius: '10px',
    transition: 'all 0.25s ease',
    '& fieldset': { borderColor: 'rgba(111,255,233,0.18)', transition: 'all 0.25s ease' },
    '&:hover fieldset': { borderColor: 'rgba(111,255,233,0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#6FFFE9', boxShadow: '0 0 14px rgba(111,255,233,0.25)' },
    '&.Mui-error fieldset': { borderColor: '#FF5252 !important' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.42)', fontSize: '0.9rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#6FFFE9' },
  '& .MuiInputLabel-root.Mui-error': { color: '#FF5252' },
  '& .MuiFormHelperText-root': { color: '#FF5252', fontSize: '0.75rem' },
  '& .MuiInputBase-input': { fontSize: '0.92rem' },
});

/* ══════════════════════════════════════════════════════════ */
/*  LOGIN COMPONENT                                           */
/* ══════════════════════════════════════════════════════════ */
const Login = ({ onLogin, switchToSignup }) => {
  const navigate = useNavigate();
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loggedRole, setLoggedRole] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });

  const closeSnack = () => setSnack(s => ({ ...s, open: false }));

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: values.email, password: values.password }),
        });
        const data = await res.json();

        if (!res.ok) throw new Error(data.message || 'Credenciales inválidas');

        /* ── persist session ── */
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('role', data.role);

        setLoggedRole(data.role);
        onLogin(data.user, data.token);

        setSnack({
          open: true,
          message: `¡Bienvenido, ${data.user.username || data.user.email}!`,
          severity: 'success',
        });

        /* ── role-based redirect ── */
        const route = ROLE_ROUTES[data.role] ?? '/';
        setTimeout(() => navigate(route), 900);

      } catch (err) {
        setSnack({ open: true, message: err.message, severity: 'error' });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(145deg, #060d1f 0%, #0B132B 45%, #111e3d 100%)',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>
      <AnimatedBackground />

      {/* ── Glass Card ──────────────────────────────────── */}
      <Box sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: 430,
        mx: 2,
        px: { xs: 3, sm: 4.5 },
        py: { xs: 3.5, sm: 5 },
        background: 'rgba(17, 30, 61, 0.6)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: '22px',
        border: '1px solid rgba(111,255,233,0.1)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
        animation: 'cardIn 0.55s cubic-bezier(0.22,1,0.36,1)',
        '@keyframes cardIn': {
          '0%': { opacity: 0, transform: 'translateY(28px) scale(0.97)' },
          '100%': { opacity: 1, transform: 'translateY(0)  scale(1)' },
        },
      }}>

        {/* ── Header ───────────────────────────────────── */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 72, height: 72,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(111,255,233,0.12) 0%, rgba(91,192,190,0.07) 100%)',
            border: '1px solid rgba(111,255,233,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2,
            boxShadow: '0 0 35px rgba(111,255,233,0.18), inset 0 1px 0 rgba(111,255,233,0.1)',
            animation: 'shieldPulse 3s ease-in-out infinite',
            '@keyframes shieldPulse': {
              '0%,100%': { boxShadow: '0 0 25px rgba(111,255,233,0.15)' },
              '50%': { boxShadow: '0 0 45px rgba(111,255,233,0.35)' },
            },
          }}>
            <Box component="img" src={`${process.env.PUBLIC_URL || ''}/iso.png`} alt="Absolluty" sx={{ width: 58, height: 58, objectFit: 'contain' }} />
          </Box>

          <Typography variant="h4" sx={{
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '1px',
            mb: 0.4,
            fontFamily: "'Inter', sans-serif",
          }}>
            ABSOLLUTY
          </Typography>

          <Typography sx={{
            color: 'rgba(255,255,255,0.38)',
            fontSize: '0.7rem',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            mb: 2.2,
          }}>
            AI Surveillance Platform
          </Typography>

          {/* Live indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%',
              background: '#4CAF50',
              animation: 'livePulse 2s ease-in-out infinite',
              '@keyframes livePulse': {
                '0%,100%': { boxShadow: '0 0 4px #4CAF50' },
                '50%': { boxShadow: '0 0 14px #4CAF50, 0 0 28px rgba(76,175,80,0.35)' },
              },
            }} />
            <Typography sx={{ color: '#4CAF50', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '1.5px' }}>
              SISTEMA ACTIVO
            </Typography>
          </Box>
        </Box>

        {/* ── Form ─────────────────────────────────────── */}
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>

          {/* Email */}
          <TextField
            fullWidth
            id="login-email"
            name="email"
            label="Usuario o email"
            autoComplete="username"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: 'rgba(111,255,233,0.45)', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={makeInputSx()}
          />

          {/* Password */}
          <TextField
            fullWidth
            id="login-password"
            name="password"
            label="Contraseña"
            type={showPwd ? 'text' : 'password'}
            autoComplete="current-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: 'rgba(111,255,233,0.45)', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    id="toggle-password-visibility"
                    onClick={() => setShowPwd(p => !p)}
                    edge="end"
                    sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#6FFFE9' } }}
                  >
                    {showPwd
                      ? <VisibilityOffIcon fontSize="small" />
                      : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={makeInputSx(1)}
          />

          {/* Forgot password link */}
          <Box sx={{ textAlign: 'right', mb: 2.5 }}>
            <Typography
              component="span"
              sx={{
                fontSize: '0.75rem',
                color: 'rgba(111,255,233,0.55)',
                cursor: 'pointer',
                transition: 'color 0.2s',
                '&:hover': { color: '#6FFFE9' },
              }}
            >
              ¿Olvidaste tu contraseña?
            </Typography>
          </Box>

          {/* Submit */}
          <Button
            id="login-submit-btn"
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              py: 1.65,
              borderRadius: '11px',
              background: loading
                ? 'rgba(111,255,233,0.08)'
                : 'linear-gradient(135deg, #6FFFE9 0%, #5BC0BE 100%)',
              color: loading ? 'rgba(111,255,233,0.4)' : '#060d1f',
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: '0.3px',
              textTransform: 'none',
              border: '1px solid rgba(111,255,233,0.25)',
              boxShadow: loading ? 'none' : '0 4px 24px rgba(111,255,233,0.28)',
              transition: 'all 0.28s ease',
              '&:hover:not(:disabled)': {
                background: 'linear-gradient(135deg, #5BC0BE 0%, #6FFFE9 100%)',
                boxShadow: '0 6px 36px rgba(111,255,233,0.48)',
                transform: 'translateY(-1px)',
              },
              '&:active:not(:disabled)': { transform: 'translateY(0)' },
            }}
          >
            {loading
              ? <CircularProgress size={22} sx={{ color: '#6FFFE9' }} />
              : 'Iniciar Sesión'}
          </Button>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.84rem' }}>
              ¿No tenés cuenta?
            </Typography>
            <Button type="button" onClick={switchToSignup} sx={{ ml: 0.5, color: '#6FFFE9', textTransform: 'none', fontWeight: 700 }}>
              Crear cuenta
            </Button>
          </Box>
        </Box>

        {/* Role badge after login */}
        {loggedRole && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <RoleBadge role={loggedRole} />
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 3.5, textAlign: 'center' }}>
          <Box sx={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(111,255,233,0.12) 50%, transparent 100%)',
            mb: 2,
          }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.68rem', letterSpacing: '1.2px' }}>
            🔒 ACCESO SEGURO · CIFRADO JWT · TLS
          </Typography>
        </Box>
      </Box>

      {/* ── Snackbar ─────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '20px !important' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={closeSnack}
          sx={{
            borderRadius: '10px',
            fontWeight: 600,
            boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
            ...(snack.severity === 'success' && {
              background: 'linear-gradient(135deg, #1a6b3c 0%, #1f8a50 100%)',
            }),
            ...(snack.severity === 'error' && {
              background: 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)',
            }),
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
